import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { FaArrowRight, FaOm, FaLeaf, FaCrown, FaAward, FaQuoteLeft } from "react-icons/fa";
import {
  FiHeart, FiPlay, FiPause, FiX, FiVolume2, FiVolumeX,
  FiChevronLeft, FiChevronRight, FiInfo, FiMaximize2, FiFilm, FiClock,
  FiUsers, FiCheckCircle,
} from "react-icons/fi";
import { GiLotus } from "react-icons/gi";

gsap.registerPlugin(ScrollTrigger);

// ══════════════════════════════════════════
// THEME   same palette as the About Us page
// ══════════════════════════════════════════
const C = {
  orange: "#F29A0C",
  gold: "#FFD166",
  deepOrange: "#E97818",
  green: "#6B8A5A",
  cream: "#FFF8EE",
  creamAlt: "#FFF3E0",
  creamLight: "#FFFDF8",
  dark: "#0A0500",
  darkAlt: "#120700",
  darkAlt2: "#1A0D00",
};

const gpu = { willChange: "transform, opacity", transform: "translateZ(0)", backfaceVisibility: "hidden" };

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  show: { opacity: 1, y: 0, transition: { duration: 0.65, ease: [0.22, 1, 0.36, 1] } },
};

const stagger = (d = 0.12) => ({ hidden: {}, show: { transition: { staggerChildren: d, delayChildren: 0.04 } } });

// ══════════════════════════════════════════
// DATA   images and videos are two separate collections
// ══════════════════════════════════════════
const IMAGE_ITEMS = [
  { id: 1, url: "/assets/gallery/image1.jpg", title: "Morning Asana Alignment", category: "Practice", desc: "Precision posture alignment and breath synchronization during our sunrise morning batch.", likes: 164 },
  { id: 2, url: "/assets/gallery/image2.jpg", title: "Classical Surya Namaskar", category: "Practice", desc: "Harmonious flow of twelve postures dedicated to solar energy and vitality.", likes: 192 },
  { id: 3, url: "/assets/gallery/image3.jpg", title: "Community Satsang Circle", category: "Community", desc: "Heart-to-heart dialogue, wisdom sharing, and unity in our weekend community gathering.", likes: 245 },
  { id: 4, url: "/assets/gallery/image4.jpg", title: "Himalayan Silence Meditation", category: "Retreats", desc: "Deep stillness amidst nature, connecting with inner peace and awareness.", likes: 218 },
  { id: 5, url: "/assets/gallery/image5.jpg", title: "Foundation Day Commemoration", category: "Events", desc: "Celebrating the sacred mission and milestone journey of Divyyog Foundation.", likes: 276 },
  { id: 6, url: "/assets/gallery/image6.jpg", title: "Pranayama & Nadi Shodhana", category: "Practice", desc: "Channel purification and nervous system balancing through guided breathwork.", likes: 183 },
  { id: 7, url: "/assets/gallery/image7.jpg", title: "Sunset Dhyana by the River", category: "Retreats", desc: "Evening mindfulness and contemplation as daylight softly transforms into twilight.", likes: 231 },
  { id: 8, url: "/assets/gallery/image8.jpg", title: "Masterclass with Lead Acharya", category: "Events", desc: "Intensive technique workshop led by international gold medalist Mrs. Divya Nithin Bhojwani.", likes: 289 },
  { id: 9, url: "/assets/gallery/image9.jpg", title: "Youth Sadhana & Agility", category: "Community", desc: "Empowering young seekers with foundational strength, posture balance, and discipline.", likes: 152 },
  { id: 10, url: "/assets/gallery/image10.jpg", title: "Vinyasa Breath & Flow", category: "Practice", desc: "Continuous dynamic movement synchronized with conscious oceanic ujjayi breath.", likes: 174 },
  { id: 11, url: "/assets/gallery/image11.jpg", title: "Nature Sanctuary Gathering", category: "Retreats", desc: "Outdoor wellness session surrounded by tranquil greenery and fresh mountain air.", likes: 205 },
  { id: 12, url: "/assets/gallery/image12.jpg", title: "International Yoga Day Celebration", category: "Events", desc: "Thousands of enthusiastic practitioners uniting under the universal banner of yoga.", likes: 298 },
  { id: 13, url: "/assets/gallery/image13.jpg", title: "Gentle Restorative Stretch", category: "Practice", desc: "Passive stretches supported by mindful breathing for deep muscular recovery.", likes: 146 },
  { id: 14, url: "/assets/gallery/image14.jpg", title: "Senior Practitioners Circle", category: "Community", desc: "Age-appropriate therapeutic practices promoting mobility, vitality, and joint ease.", likes: 169 },
  { id: 15, url: "/assets/gallery/image15.jpg", title: "Sunrise Mountain Contemplation", category: "Retreats", desc: "Greeting the dawn with inner gratitude, dhyana, and silent mantra chanting.", likes: 224 },
  { id: 16, url: "/assets/gallery/image16.jpg", title: "Championship Honors & Felicitation", category: "Events", desc: "Recognizing exemplary dedication, yogic excellence, and student achievements.", likes: 263 },
  { id: 17, url: "/assets/gallery/image17.jpg", title: "Bhakti Yoga & Chanting Evening", category: "Community", desc: "Uplifting kirtan, traditional mantras, and sacred acoustic vibrations.", likes: 197 },
  { id: 18, url: "/assets/gallery/image18.jpg", title: "Therapeutic Spinal Alignment", category: "Practice", desc: "Specialized sequences designed for postural correction and chronic back ease.", likes: 188 },
  { id: 19, url: "/assets/gallery/image19.jpg", title: "Ashram Living & Mindful Living", category: "Retreats", desc: "Experiencing disciplined simplicity, sattvic nutrition, and spiritual brotherhood.", likes: 242 },
  { id: 20, url: "/assets/gallery/image20.jpg", title: "Vedic Heritage Workshop", category: "Events", desc: "Exploring classical yogic texts, philosophy, and Patanjali's Yoga Sutras.", likes: 215 },
  { id: 21, url: "/assets/gallery/image21.jpg", title: "Warrior Strength & Grounding", category: "Practice", desc: "Cultivating stability, focus, and core root strength in standing postures.", likes: 161 },
  { id: 22, url: "/assets/gallery/image22.jpg", title: "Women's Wellness Circle", category: "Community", desc: "Hormonal balance, pelvic health, and mindful care tailored for women's well-being.", likes: 208 },
  { id: 23, url: "/assets/gallery/image23.jpg", title: "Lakefront Sunrise Meditation", category: "Retreats", desc: "Mirror-still waters reflecting the calm clarity achieved through sustained dhyana.", likes: 235 },
  { id: 24, url: "/assets/gallery/image24.jpg", title: "Annual Sadhana Conclave", category: "Events", desc: "Gathering of certified instructors, practitioners, and guest spiritual mentors.", likes: 271 },
  { id: 25, url: "/assets/gallery/image25.jpg", title: "Inversions & Balance Mastery", category: "Practice", desc: "Developing spatial awareness, courage, and core control in inverted asanas.", likes: 179 },
  { id: 26, url: "/assets/gallery/image26.jpg", title: "Family Yoga & Bonding Day", category: "Community", desc: "Joyful multi-generational sessions fostering connection, fun, and wellness together.", likes: 194 },
  { id: 27, url: "/assets/gallery/image27.jpg", title: "Silent Forest Walk Sadhana", category: "Retreats", desc: "Practicing walking meditation with mindful steps and sensory attunement.", likes: 221 },
  { id: 28, url: "/assets/gallery/image28.jpg", title: "Guru Purnima Celebrations", category: "Events", desc: "Expressing gratitude and reverence to the timeless lineage of sacred yoga masters.", likes: 284 },
  { id: 29, url: "/assets/gallery/image29.jpg", title: "Deep Nidra Conscious Rest", category: "Practice", desc: "Systematic guided relaxation exploring the borderland between sleep and meditation.", likes: 175 },
  { id: 30, url: "/assets/gallery/image30.jpg", title: "Fellowship & Community Breakfast", category: "Community", desc: "Sattvic post-practice nourishing meal and warm community bonding.", likes: 186 },
  { id: 31, url: "/assets/gallery/image31.jpg", title: "Himalayan Valley Retreat", category: "Retreats", desc: "Immersion in pure mountain prana, silent walks, and intensive yogic sadhana.", likes: 259 },
  { id: 32, url: "/assets/gallery/image32.jpg", title: "Yoga Olympiad Participation", category: "Events", desc: "Students demonstrating artistic yoga, traditional poses, and disciplined grace.", likes: 248 },
  { id: 33, url: "/assets/gallery/image33.jpg", title: "Seated Forward Fold Stillness", category: "Practice", desc: "Surrendering into the fold, calming the brain, and soothing the central nervous system.", likes: 158 },
  { id: 34, url: "/assets/gallery/image34.jpg", title: "Gratitude Sharing Circle", category: "Community", desc: "Reflections from members on their healing transformations and inner breakthroughs.", likes: 191 },
  { id: 35, url: "/assets/gallery/image35.jpg", title: "Sacred Fire Havan Ritual", category: "Retreats", desc: "Traditional yajna for mental purification, positive energy, and peace.", likes: 229 },
  { id: 36, url: "/assets/gallery/image36.jpg", title: "Medal Presentation Ceremony", category: "Events", desc: "Commemorating world championship gold medal victories and national records.", likes: 295 },
  { id: 37, url: "/assets/gallery/image37.jpg", title: "Core Stability & Bandha Control", category: "Practice", desc: "Activating the pelvic and abdominal locks for energetic stability and lightness.", likes: 167 },
  { id: 38, url: "/assets/gallery/image38.jpg", title: "Outdoor Park Yoga Meetup", category: "Community", desc: "Spreading the joy of practice in open public gardens on a breezy Sunday morning.", likes: 182 },
  { id: 39, url: "/assets/gallery/image39.jpg", title: "Riverfront Twilight Dhyana", category: "Retreats", desc: "The sound of gentle flowing water complementing deep rhythmic breathing.", likes: 214 },
  { id: 40, url: "/assets/gallery/image40.jpg", title: "Teacher Training Graduation", category: "Events", desc: "New cohort of certified 200hr and 500hr instructors stepping into service.", likes: 267 },
  { id: 41, url: "/assets/gallery/image41.jpg", title: "Heart Opening Backbends", category: "Practice", desc: "Releasing emotional tension and expanding thoracic lung capacity with care.", likes: 172 },
  { id: 42, url: "/assets/gallery/image42.jpg", title: "Inclusive Adaptive Practice", category: "Community", desc: "Modified chair and wall yoga making the benefits of practice accessible to everyone.", likes: 163 },
  { id: 43, url: "/assets/gallery/image43.jpg", title: "Starlit Night Meditation", category: "Retreats", desc: "Gazing upon open skies, expanding cosmic awareness, and cultivating silence.", likes: 238 },
  { id: 44, url: "/assets/gallery/image44.jpg", title: "Studio Anniversary Gala", category: "Events", desc: "Reflecting upon years of continuous community service, health, and enlightenment.", likes: 279 },
  { id: 45, url: "/assets/gallery/image45.jpg", title: "Standing Balance & Focus", category: "Practice", desc: "Steadfast gaze (drishti) anchoring physical equilibrium and single-pointed mind.", likes: 155 },
  { id: 46, url: "/assets/gallery/image46.jpg", title: "Peer Mentorship Exchange", category: "Community", desc: "Senior students supporting beginners with props, alignment cues, and encouragement.", likes: 178 },
  { id: 47, url: "/assets/gallery/image47.jpg", title: "Monsoon Mountain Retreat", category: "Retreats", desc: "Nourishing rain, lush greenery, and rejuvenating indoor pranayama chambers.", likes: 246 },
  { id: 48, url: "/assets/gallery/image48.jpg", title: "Holistic Health Expo", category: "Events", desc: "Showcasing ayurvedic diet plans, lifestyle alignment, and preventive wellness.", likes: 225 },
  { id: 49, url: "/assets/gallery/image49.jpg", title: "Hip Mobility & Tension Release", category: "Practice", desc: "Releasing deep seated fascial tension through restorative seated postures.", likes: 166 },
  { id: 50, url: "/assets/gallery/image50.jpg", title: "Volunteers & Seva Team", category: "Community", desc: "The dedicated hands and hearts that selflessly keep our daily classes organized.", likes: 201 },
  { id: 51, url: "/assets/gallery/image51.jpg", title: "Forest Sanctuary Immersion", category: "Retreats", desc: "Unplugged digital detox surrounded by ancient pine trees and natural silence.", likes: 252 },
  { id: 52, url: "/assets/gallery/image52.jpg", title: "Championship Trophy Showcase", category: "Events", desc: "Displaying international awards and certificates earned by Divyyog athletes.", likes: 291 },
  { id: 53, url: "/assets/gallery/image53.jpg", title: "Arm Balance & Concentration", category: "Practice", desc: "Cultivating muscular coordination, wrist resilience, and unshakeable poise.", likes: 184 },
  { id: 54, url: "/assets/gallery/image54.jpg", title: "Celebration of Milestones", category: "Community", desc: "Marking personal health victories: reversed ailments, stamina, and inner joy.", likes: 196 },
  { id: 55, url: "/assets/gallery/image55.jpg", title: "Sunrise Pranayama on the Ridge", category: "Retreats", desc: "Greeting the cool morning breeze with bellows breath and mental illumination.", likes: 233 },
  { id: 56, url: "/assets/gallery/image56.jpg", title: "World Health Day Seminar", category: "Events", desc: "Public educational panel on non-invasive healing, breathwork, and posture health.", likes: 219 },
  { id: 57, url: "/assets/gallery/image57.jpg", title: "Flowing Moon Salutations", category: "Practice", desc: "Cooling lunar sequence calming the mind and preparing the body for evening rest.", likes: 162 },
  { id: 58, url: "/assets/gallery/image58.jpg", title: "Circle of Light & Fellowship", category: "Community", desc: "Gathering with lit diyas, quiet contemplation, and heartfelt togetherness.", likes: 212 },
  { id: 59, url: "/assets/gallery/image59.jpg", title: "Mountain Spring Meditation", category: "Retreats", desc: "Pure alpine streams serenading deep contemplative dhyana sessions.", likes: 240 },
  { id: 60, url: "/assets/gallery/image60.jpg", title: "Gold Medalist Welcome Ceremony", category: "Events", desc: "Homecoming celebration honoring international victories and studio pride.", likes: 287 },
  { id: 61, url: "/assets/gallery/image61.jpg", title: "Side Stretch & Lateral Opening", category: "Practice", desc: "Lengthening intercostal muscles to deepen natural ribcage respiration.", likes: 159 },
  { id: 62, url: "/assets/gallery/image62.jpg", title: "Open Q&A with Senior Faculty", category: "Community", desc: "Instructors answering lifestyle, nutrition, and personal practice queries.", likes: 187 },
  { id: 63, url: "/assets/gallery/image63.jpg", title: "Valley Sunrise Chanting", category: "Retreats", desc: "Echoing sacred vibrations across the mountain slopes in early morning light.", likes: 256 },
  { id: 64, url: "/assets/gallery/image64.jpg", title: "Community Service Outreach", category: "Events", desc: "Extending free yoga and wellness sessions to underprivileged neighborhoods.", likes: 273 },
  { id: 65, url: "/assets/gallery/image65.jpg", title: "Meditation on Sound & Vibration", category: "Practice", desc: "Exploring subtle inner vibrations through humming bhramari pranayama.", likes: 177 },
  { id: 66, url: "/assets/gallery/image66.jpg", title: "Festive Diwali Sadhana Meet", category: "Community", desc: "Celebrating the victory of light and awareness over inner darkness.", likes: 228 },
  { id: 67, url: "/assets/gallery/image67.jpg", title: "Sacred Solitude Retreat", category: "Retreats", desc: "Dedicated silent reflection fostering profound personal clarity and renewal.", likes: 264 },
  { id: 68, url: "/assets/gallery/image68.jpg", title: "Foundation Gala Finale", category: "Events", desc: "The grand culmination of our annual gathering, united in love and sadhana.", likes: 310 },
];

const VIDEO_ITEMS = [
  { id: 101, url: "https://assets.mixkit.co/videos/preview/mixkit-woman-doing-yoga-on-a-wooden-floor-41617-large.mp4", thumbnail: "/assets/gallery/image2.jpg", title: "Morning Session Highlights", category: "Practice", desc: "A look inside a typical Divyyog Foundation morning class.", duration: "2:14" },
  { id: 102, url: "https://assets.mixkit.co/videos/preview/mixkit-woman-meditating-by-the-sea-41604-large.mp4", thumbnail: "/assets/gallery/image3.jpg", title: "Retreat Recap: The Coast", category: "Retreats", desc: "Highlights from our oceanfront retreat weekend.", duration: "3:02" },
  { id: 103, url: "https://assets.mixkit.co/videos/preview/mixkit-stretching-exercises-in-a-sunny-room-41611-large.mp4", thumbnail: "/assets/gallery/image4.jpg", title: "Community Class Recording", category: "Community", desc: "Full recording from a recent open community session.", duration: "4:36" },
];

const CATEGORIES = ["All", "Practice", "Community", "Retreats", "Events"];

// ══════════════════════════════════════════
// Ambient background   quiet, restrained
// ══════════════════════════════════════════
function GradientMesh() {
  return (
    <>
      <motion.div
        animate={{ y: [0, -25, 0] }}
        transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
        className="absolute top-[-15%] right-[-10%] w-[600px] h-[600px] rounded-full pointer-events-none"
        style={{ background: `radial-gradient(circle, ${C.orange}12, transparent 70%)` }}
      />
      <motion.div
        animate={{ y: [0, 20, 0] }}
        transition={{ duration: 17, repeat: Infinity, ease: "easeInOut" }}
        className="absolute bottom-[-15%] left-[-10%] w-[500px] h-[500px] rounded-full pointer-events-none"
        style={{ background: `radial-gradient(circle, ${C.green}0f, transparent 70%)` }}
      />
    </>
  );
}

// ══════════════════════════════════════════
// Filter pill
// ══════════════════════════════════════════
function FilterPill({ cat, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className="relative px-6 py-3 rounded-full text-[13px] font-bold tracking-wide transition-colors border-none cursor-pointer bg-transparent outline-none"
    >
      {active && (
        <motion.div
          layoutId="activeFilterPill"
          className="absolute inset-0 rounded-full"
          style={{ background: `linear-gradient(120deg, ${C.orange}, ${C.deepOrange})`, boxShadow: `0 8px 22px ${C.orange}45` }}
          transition={{ type: "spring", stiffness: 380, damping: 30 }}
        />
      )}
      <span className={`relative z-10 transition-colors duration-300 ${active ? "text-white" : "text-[#3a2a18] hover:text-[#F29A0C]"}`}>{cat}</span>
    </button>
  );
}

// ══════════════════════════════════════════
// Heart-burst micro interaction
// ══════════════════════════════════════════
function HeartBurst({ fire }) {
  if (!fire) return null;
  return (
    <span className="absolute -top-1 left-1/2 -translate-x-1/2 pointer-events-none">
      {Array.from({ length: 6 }).map((_, i) => {
        const angle = (i / 6) * Math.PI * 2;
        return (
          <motion.span
            key={i}
            initial={{ opacity: 1, x: 0, y: 0, scale: 0.6 }}
            animate={{ opacity: 0, x: Math.cos(angle) * 24, y: Math.sin(angle) * 24 - 8, scale: 1 }}
            transition={{ duration: 0.55, ease: "easeOut" }}
            className="absolute w-1.5 h-1.5 rounded-full"
            style={{ background: i % 2 === 0 ? C.orange : C.gold }}
          />
        );
      })}
    </span>
  );
}

// ══════════════════════════════════════════
// Image card   framed "gallery plate" design: matted image + editorial caption panel below.
// Wrapped in an always-on, infinitely rotating gradient-ring frame for a modern, premium feel.
// ══════════════════════════════════════════
function GalleryCard({ item, index, onSelect }) {
  const cardRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);
  const [liked, setLiked] = useState(false);
  const [burst, setBurst] = useState(false);
  const [likesCount, setLikesCount] = useState(item.likes);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [flash, setFlash] = useState(false);

  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const qx = gsap.quickTo(el, "rotateY", { duration: 0.7, ease: "power3.out" });
    const qy = gsap.quickTo(el, "rotateX", { duration: 0.7, ease: "power3.out" });
    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      const px = (e.clientX - r.left) / r.width;
      const py = (e.clientY - r.top) / r.height;
      qx((px - 0.5) * 7);
      qy((py - 0.5) * -7);
    };
    const onLeave = () => {
      qx(0);
      qy(0);
    };
    el.addEventListener("mousemove", onMove);
    el.addEventListener("mouseleave", onLeave);
    return () => {
      el.removeEventListener("mousemove", onMove);
      el.removeEventListener("mouseleave", onLeave);
    };
  }, []);

  const toggleLike = (e) => {
    e.stopPropagation();
    setLikesCount((p) => (liked ? p - 1 : p + 1));
    setLiked(!liked);
    setBurst(true);
    setTimeout(() => setBurst(false), 600);
  };

  const handleClick = () => {
    setFlash(true);
    setTimeout(() => setFlash(false), 450);
    onSelect(item);
  };

  return (
    <motion.div
      className="gallery-tile break-inside-avoid mb-9"
      initial={{ opacity: 0, y: 36 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.6, delay: (index % 4) * 0.06, ease: [0.16, 1, 0.3, 1] }}
    >
      {/* ── Gradient-ring frame   the "always on" infinite rotating border lives here ── */}
      <div
        className="relative rounded-[28px] p-[2px] gradient-ring"
        style={{
          "--ring-opacity": isHovered ? 1 : 0.75,
          "--ring-speed": isHovered ? "3.2s" : "6.5s",
        }}
      >
        {/* soft blurred glow duplicate behind the card, deepens on hover */}
        <div
          className="absolute -inset-3 rounded-[34px] opacity-40 group-hover:opacity-70 blur-2xl pointer-events-none transition-opacity duration-500 gradient-ring-glow"
          style={{ "--ring-speed": isHovered ? "3.2s" : "6.5s" }}
        />

        <div
          ref={cardRef}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          className="group relative rounded-[26px] bg-white p-3 cursor-pointer"
          style={{
            boxShadow: isHovered ? "0 34px 70px rgba(18,7,0,0.16), 0 2px 0 rgba(242,154,12,0.25)" : "0 10px 30px rgba(18,7,0,0.07)",
            transform: isHovered ? "translateY(-5px)" : "translateY(0)",
            transformStyle: "preserve-3d",
            perspective: "1000px",
            transition: "box-shadow 0.5s cubic-bezier(0.16,1,0.3,1), transform 0.5s cubic-bezier(0.16,1,0.3,1)",
            willChange: "transform",
          }}
        >
          {/* mat / frame   image sits inset with a thin hairline border, like a printed plate */}
          <div
            className="relative rounded-[18px] overflow-hidden"
            onClick={handleClick}
            style={{ boxShadow: `inset 0 0 0 1px ${C.orange}30` }}
          >
            {/* diagonal glass-shine sweep, hover only */}
            <div
              className="absolute inset-0 z-20 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-500"
              style={{
                background: "linear-gradient(115deg, transparent 20%, rgba(255,255,255,0.35) 42%, transparent 60%)",
                transform: isHovered ? "translateX(60%)" : "translateX(-60%)",
                transition: "transform 1.1s cubic-bezier(0.16,1,0.3,1), opacity 0.4s ease",
              }}
            />

            <AnimatePresence>
              {flash && (
                <motion.div
                  initial={{ opacity: 0.8 }}
                  animate={{ opacity: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.45, ease: "easeOut" }}
                  className="absolute inset-0 z-40 pointer-events-none bg-white"
                />
              )}
            </AnimatePresence>

            <div
              className={`absolute inset-0 flex items-center justify-center transition-opacity duration-500 z-10 ${imageLoaded ? "opacity-0 pointer-events-none" : "opacity-100"}`}
              style={{ background: `linear-gradient(135deg, ${C.creamLight}, ${C.creamAlt})`, minHeight: 190 }}
            >
              <div className="w-8 h-8 rounded-full border-4 animate-spin" style={{ borderColor: `${C.orange}30`, borderTopColor: C.orange }} />
            </div>

            <img
              src={item.url}
              alt={item.title}
              loading="lazy"
              onLoad={() => setImageLoaded(true)}
              className="block w-full h-auto relative z-0"
              style={{
                transform: isHovered ? "scale(1.06)" : "scale(1)",
                transition: "transform 0.9s cubic-bezier(0.16,1,0.3,1)",
                ...gpu,
              }}
            />

            {/* catalog plate number */}
            <div
              className="absolute top-3 right-3 z-20 px-2.5 py-1 rounded-[8px] text-[10px] font-bold tracking-wide backdrop-blur-md"
              style={{ background: "rgba(10,5,0,0.5)", color: C.gold, fontFamily: "'Poppins',sans-serif" }}
            >
              Plate {String(index + 1).padStart(2, "0")}
            </div>

            {/* like pill, always visible, top-left over the image */}
            <div className="absolute top-3 left-3 z-20">
              <button
                onClick={toggleLike}
                className="relative flex items-center gap-1.5 px-3 py-1.5 rounded-full backdrop-blur-md border-none cursor-pointer text-[11px] font-bold transition-colors"
                style={{ background: liked ? "#E24B4A" : "rgba(10,5,0,0.5)", color: "#fff" }}
              >
                <FiHeart className={liked ? "fill-white text-white" : ""} size={12} />
                <span>{likesCount}</span>
                <HeartBurst fire={burst} />
              </button>
            </div>
          </div>

          {/* caption panel   editorial, always legible, not overlaid on the image */}
          <div className="px-2 pt-4 pb-2">
            <div className="flex items-center justify-between gap-3">
              <span className="text-[10px] font-bold uppercase tracking-[2px]" style={{ color: C.green }}>{item.category}</span>
              <span className="h-px flex-1" style={{ background: `linear-gradient(90deg, ${C.orange}35, transparent)` }} />
            </div>

            <h3
              onClick={handleClick}
              className="font-semibold mt-2 leading-snug cursor-pointer transition-colors duration-300 group-hover:text-[#F29A0C]"
              style={{ fontFamily: "'Poppins',sans-serif", fontSize: "18px", color: C.darkAlt }}
            >
              {item.title}
            </h3>

            <p className="text-[13px] mt-1.5 leading-relaxed" style={{ color: "#8a7a68" }}>
              {item.desc}
            </p>

            <button
              onClick={handleClick}
              className="group/link inline-flex items-center gap-1.5 mt-3.5 text-[12px] font-bold cursor-pointer border-none bg-transparent p-0"
              style={{ color: C.deepOrange }}
            >
              <span className="relative">
                View Full Resolution
                <span className="absolute left-0 -bottom-0.5 h-[1.5px] w-full origin-left scale-x-0 group-hover/link:scale-x-100 transition-transform duration-300" style={{ background: C.deepOrange }} />
              </span>
              <FiMaximize2 size={12} className="transition-transform duration-300 group-hover/link:translate-x-0.5" />
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// ══════════════════════════════════════════
// Featured Videos   main player + playlist, replaces the old marquee entirely
// ══════════════════════════════════════════
function FeaturedVideos({ onExpand }) {
  const [active, setActive] = useState(VIDEO_ITEMS[0]);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const [progress, setProgress] = useState(0);
  const videoRef = useRef(null);

  const selectVideo = (item) => {
    setActive(item);
    setPlaying(false);
    setProgress(0);
  };

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play();
      setPlaying(true);
    } else {
      v.pause();
      setPlaying(false);
    }
  };

  const toggleMute = (e) => {
    e.stopPropagation();
    setMuted((m) => !m);
  };

  const onTimeUpdate = () => {
    const v = videoRef.current;
    if (!v || !v.duration) return;
    setProgress((v.currentTime / v.duration) * 100);
  };

  return (
    <section id="video-library" className="relative overflow-hidden py-24 px-6 lg:px-16" style={{ background: `linear-gradient(160deg, ${C.darkAlt2}, ${C.dark})` }}>
      <div className="absolute inset-0 opacity-40 pointer-events-none" style={{ background: `radial-gradient(ellipse at 50% 0%, ${C.orange}1c, transparent 60%)` }} />

      <div className="relative z-10 max-w-[1400px] mx-auto">
        <div className="flex items-center gap-4 mb-14">
          <div className="w-11 h-11 rounded-[14px] flex items-center justify-center flex-shrink-0" style={{ background: `linear-gradient(135deg,${C.orange},${C.deepOrange})` }}>
            <FiFilm size={18} className="text-white" />
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[3px]" style={{ color: C.orange }}>Video Library</p>
            <h2 className="text-white text-[28px] md:text-[38px] font-semibold" style={{ fontFamily: "Poppins" }}>Featured Videos</h2>
          </div>
        </div>

        <div className="grid lg:grid-cols-[1fr_360px] gap-8 items-start">
          {/* main player */}
          <div className="relative rounded-[28px] overflow-hidden border" style={{ borderColor: "rgba(255,255,255,0.08)", boxShadow: "0 40px 100px rgba(0,0,0,0.35)" }}>
            <AnimatePresence mode="wait">
              <motion.div
                key={active.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4 }}
                className="relative w-full"
                style={{ aspectRatio: "16/9", background: C.dark }}
              >
                <video
                  ref={videoRef}
                  key={active.id}
                  src={active.url}
                  poster={active.thumbnail}
                  muted={muted}
                  playsInline
                  onTimeUpdate={onTimeUpdate}
                  onPlay={() => setPlaying(true)}
                  onPause={() => setPlaying(false)}
                  onEnded={() => setPlaying(false)}
                  onClick={togglePlay}
                  className="absolute inset-0 w-full h-full object-cover cursor-pointer"
                />

                {/* play / pause center control, fades out once playing */}
                <div
                  className="absolute inset-0 flex items-center justify-center pointer-events-none transition-opacity duration-400"
                  style={{ opacity: playing ? 0 : 1, background: playing ? "transparent" : "rgba(10,5,0,0.25)" }}
                >
                  <button
                    onClick={togglePlay}
                    className="pointer-events-auto w-20 h-20 rounded-full flex items-center justify-center border cursor-pointer backdrop-blur-md transition-transform duration-300 hover:scale-110"
                    style={{ background: "rgba(255,255,255,0.16)", borderColor: "rgba(255,255,255,0.3)" }}
                  >
                    <FiPlay size={28} className="text-white ml-1" />
                  </button>
                </div>

                {/* top controls */}
                <div className="absolute top-5 right-5 flex items-center gap-2 z-10">
                  <button
                    onClick={toggleMute}
                    className="w-10 h-10 rounded-full flex items-center justify-center backdrop-blur-md border cursor-pointer transition-colors"
                    style={{ background: "rgba(10,5,0,0.5)", borderColor: "rgba(255,255,255,0.12)" }}
                  >
                    {muted ? <FiVolumeX size={15} className="text-white" /> : <FiVolume2 size={15} className="text-white" />}
                  </button>
                  <button
                    onClick={() => onExpand(active)}
                    className="w-10 h-10 rounded-full flex items-center justify-center backdrop-blur-md border cursor-pointer transition-colors"
                    style={{ background: "rgba(10,5,0,0.5)", borderColor: "rgba(255,255,255,0.12)" }}
                  >
                    <FiMaximize2 size={14} className="text-white" />
                  </button>
                </div>

                {/* progress bar */}
                <div className="absolute bottom-0 left-0 right-0 h-1" style={{ background: "rgba(255,255,255,0.15)" }}>
                  <div className="h-full" style={{ width: `${progress}%`, background: `linear-gradient(90deg, ${C.orange}, ${C.gold})`, transition: "width 0.2s linear" }} />
                </div>
              </motion.div>
            </AnimatePresence>

            <div className="p-7" style={{ background: C.darkAlt }}>
              <div className="flex items-center gap-3 mb-2">
                <span className="text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full" style={{ background: `${C.orange}22`, color: C.gold }}>
                  {active.category}
                </span>
                <span className="flex items-center gap-1 text-white/40 text-[12px]">
                  <FiClock size={12} /> {active.duration}
                </span>
              </div>
              <h3 className="text-white text-[20px] font-semibold" style={{ fontFamily: "Poppins" }}>{active.title}</h3>
              <p className="text-white/55 text-[13.5px] mt-2 leading-relaxed max-w-[560px]">{active.desc}</p>
            </div>
          </div>

          {/* playlist */}
          <div className="flex flex-col gap-3">
            <p className="text-white/40 text-[11px] font-bold uppercase tracking-widest px-1 mb-1">Up Next</p>
            {VIDEO_ITEMS.map((v) => {
              const isActive = v.id === active.id;
              return (
                <button
                  key={v.id}
                  onClick={() => selectVideo(v)}
                  className="relative flex items-center gap-4 p-3 rounded-[18px] text-left border transition-all duration-300 cursor-pointer"
                  style={{
                    background: isActive ? "rgba(242,154,12,0.1)" : "rgba(255,255,255,0.03)",
                    borderColor: isActive ? `${C.orange}55` : "rgba(255,255,255,0.06)",
                  }}
                >
                  {isActive && <span className="absolute left-0 top-3 bottom-3 w-[3px] rounded-full" style={{ background: `linear-gradient(to bottom, ${C.orange}, ${C.gold})` }} />}
                  <div className="relative w-24 h-16 rounded-[12px] overflow-hidden flex-shrink-0">
                    <img src={v.thumbnail} alt={v.title} className="w-full h-full object-cover" loading="lazy" />
                    <div className="absolute inset-0 flex items-center justify-center" style={{ background: "rgba(10,5,0,0.35)" }}>
                      {isActive && playing ? <FiPause size={14} className="text-white" /> : <FiPlay size={14} className="text-white" />}
                    </div>
                  </div>
                  <div className="min-w-0">
                    <p className="text-white text-[13.5px] font-semibold truncate" style={{ fontFamily: "Poppins" }}>{v.title}</p>
                    <p className="text-white/40 text-[11px] mt-1">{v.category} · {v.duration}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

// ══════════════════════════════════════════
// Lightbox   full-screen viewer for the image grid
// ══════════════════════════════════════════
function Lightbox({ activeItem, items, onClose, onPrev, onNext, onJump }) {
  if (!activeItem) return null;
  const idx = items.findIndex((x) => x.id === activeItem.id);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[9999] flex flex-col justify-between p-6 md:p-10"
      style={{ background: `${C.dark}fa`, backdropFilter: "blur(20px)" }}
    >
      <div className="flex justify-between items-center w-full relative z-10">
        <div className="flex items-center gap-3 border px-5 py-2.5 rounded-full backdrop-blur-md" style={{ background: "rgba(255,255,255,0.05)", borderColor: "rgba(255,255,255,0.1)" }}>
          <FiInfo style={{ color: C.orange }} size={14} />
          <span className="text-[12px] font-bold text-white uppercase tracking-wider">{activeItem.category}</span>
          <span className="text-white/30">|</span>
          <span className="text-[12px] text-white/70">{idx + 1} of {items.length}</span>
        </div>
        <button onClick={onClose} className="w-12 h-12 rounded-full text-white flex items-center justify-center border-none cursor-pointer backdrop-blur-md" style={{ background: "rgba(255,255,255,0.1)" }}>
          <FiX size={20} />
        </button>
      </div>

      <div className="flex-1 flex items-center justify-center relative w-full my-6">
        <button onClick={onPrev} className="absolute left-0 md:left-4 z-10 w-14 h-14 rounded-full text-white flex items-center justify-center border-none cursor-pointer backdrop-blur-md" style={{ background: "rgba(255,255,255,0.1)" }}>
          <FiChevronLeft size={24} />
        </button>

        <AnimatePresence mode="wait">
          <motion.div
            key={activeItem.id}
            initial={{ opacity: 0, scale: 0.95, y: 15 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -15 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="max-w-[90%] max-h-[68vh] rounded-[24px] overflow-hidden border relative flex items-center justify-center"
            style={{ borderColor: "rgba(255,255,255,0.1)" }}
          >
            {activeItem.url && activeItem.duration !== undefined ? (
              <video src={activeItem.url} controls autoPlay className="max-w-full max-h-[68vh] object-contain rounded-[24px]" />
            ) : (
              <img src={activeItem.url} alt={activeItem.title} className="max-w-full max-h-[68vh] object-contain rounded-[24px]" />
            )}
          </motion.div>
        </AnimatePresence>

        <button onClick={onNext} className="absolute right-0 md:right-4 z-10 w-14 h-14 rounded-full text-white flex items-center justify-center border-none cursor-pointer backdrop-blur-md" style={{ background: "rgba(255,255,255,0.1)" }}>
          <FiChevronRight size={24} />
        </button>
      </div>

      <div className="w-full max-w-3xl mx-auto text-center relative z-10">
        <h3 className="text-white text-[22px] font-bold" style={{ fontFamily: "Poppins" }}>{activeItem.title}</h3>
        <p className="text-white/60 text-[13.5px] mt-2 max-w-[600px] mx-auto leading-relaxed">{activeItem.desc}</p>

        <div className="flex justify-center gap-2.5 mt-6 overflow-x-auto pb-1">
          {items.map((it) => (
            <button key={it.id} onClick={() => onJump(it)} className="relative w-14 h-11 rounded-[8px] overflow-hidden flex-shrink-0 border-2 transition-all"
              style={{ borderColor: it.id === activeItem.id ? C.orange : "rgba(255,255,255,0.15)", opacity: it.id === activeItem.id ? 1 : 0.5 }}>
              <img src={it.thumbnail || it.url} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      </div>
    </motion.div>
  );
}

// ══════════════════════════════════════════
// Main Gallery page
// ══════════════════════════════════════════
const Gallery = () => {
  const [selectedCat, setSelectedCat] = useState("All");
  const [activeItem, setActiveItem] = useState(null);
  const [isMobile, setIsMobile] = useState(true);
  const gridRef = useRef(null);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 1024);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Parallax   runs on compositor thread via CSS custom property, matches About / Home / Benefits hero
  const heroRef = useRef(null);
  const { scrollYProgress: heroScroll } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroY = useTransform(heroScroll, [0, 1], ["0%", "15%"]);
  const heroOpacity = useTransform(heroScroll, [0, 0.65], [1, 0]);

  const filteredItems = selectedCat === "All" ? IMAGE_ITEMS : IMAGE_ITEMS.filter((i) => i.category === selectedCat);

  // ── "View More" pagination: 5 rows at a time (matches the 1 / 2 / 3 column masonry) ──
  const ROWS_PER_PAGE = 5;
  const getPageSize = () => {
    if (typeof window === "undefined") return 15;
    if (window.innerWidth >= 1024) return 3 * ROWS_PER_PAGE;   // lg: 3 columns → 15
    if (window.innerWidth >= 640) return 2 * ROWS_PER_PAGE;    // sm: 2 columns → 10
    return 6;                                                   // phones: 1 column → 6
  };
  const [pageSize, setPageSize] = useState(getPageSize);
  const [visibleCount, setVisibleCount] = useState(getPageSize);

  useEffect(() => {
    const onResize = () => setPageSize(getPageSize());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Start from the first page again when the filter or column count changes
  useEffect(() => { setVisibleCount(pageSize); }, [selectedCat, pageSize]);

  const visibleItems = filteredItems.slice(0, visibleCount);
  const hasMore = visibleCount < filteredItems.length;
  const canCollapse = !hasMore && filteredItems.length > pageSize;

  const handleViewMore = () => setVisibleCount((c) => Math.min(c + pageSize, filteredItems.length));
  const handleShowLess = () => {
    setVisibleCount(pageSize);
    const el = gridRef.current;
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - 140;
      if (window.lenis?.scrollTo) window.lenis.scrollTo(top, { duration: 1 });
      else window.scrollTo({ top, behavior: "smooth" });
    }
  };

  const handlePrev = () => {
    const list = activeItem.duration !== undefined ? VIDEO_ITEMS : IMAGE_ITEMS;
    const i = list.findIndex((x) => x.id === activeItem.id);
    setActiveItem(list[(i - 1 + list.length) % list.length]);
  };
  const handleNext = () => {
    const list = activeItem.duration !== undefined ? VIDEO_ITEMS : IMAGE_ITEMS;
    const i = list.findIndex((x) => x.id === activeItem.id);
    setActiveItem(list[(i + 1) % list.length]);
  };

  useEffect(() => {
    if (!activeItem) return;
    const onKey = (e) => {
      if (e.key === "Escape") setActiveItem(null);
      if (e.key === "ArrowLeft") handlePrev();
      if (e.key === "ArrowRight") handleNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [activeItem]);

  return (
    <div className="relative overflow-hidden" style={{ background: C.cream }}>
      {/* Local keyframes   infinite rotating gradient ring for gallery cards */}
      <style dangerouslySetInnerHTML={{
        __html: `
        @keyframes ring-rotate {
          to { --ring-angle: 360deg; }
        }
        @property --ring-angle {
          syntax: '<angle>';
          initial-value: 0deg;
          inherits: false;
        }
        .gradient-ring {
          --ring-angle: 0deg;
          background: conic-gradient(from var(--ring-angle),
            ${C.orange}, ${C.gold}, ${C.deepOrange}, transparent 45%, transparent 55%, ${C.orange});
          animation: ring-rotate var(--ring-speed, 6.5s) linear infinite;
          opacity: var(--ring-opacity, 0.75);
          transition: opacity 0.4s ease;
        }
        .gradient-ring-glow {
          --ring-angle: 0deg;
          background: conic-gradient(from var(--ring-angle),
            ${C.orange}, ${C.gold}, ${C.deepOrange}, transparent 45%, transparent 55%, ${C.orange});
          animation: ring-rotate var(--ring-speed, 6.5s) linear infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .gradient-ring, .gradient-ring-glow { animation: none; }
        }
      `}} />

      {/* ══════════════════════════════════════════
          HERO — FULL VIEWPORT OCCUPATION (DYNAMIC 100DVH / 100VW)
      ══════════════════════════════════════════ */}
      <section 
        ref={heroRef} 
        className="relative w-full min-h-screen min-h-[100dvh] flex items-center justify-center overflow-hidden" 
        style={{ background: C.dark }}
      >
        {/* Background Image — dynamically covers 100% width and height without gaps */}
        <div className="absolute inset-0 w-full h-full z-0 overflow-hidden">
          <img 
            src="/assets/glimpses_hero_bg.jpg" 
            alt="Sacred Yogic Sanctuary" 
            className="w-full h-full object-cover object-center scale-[1.04]"
            style={{ filter: "brightness(0.66) saturate(1.2)" }}
          />
        </div>

        {/* ── OVERLAPPING RICH WARM ESPRESSO BROWN, SAFFRON ORANGE & SACRED OLIVE GREEN TINTS ── */}
        <div 
          className="absolute inset-0 pointer-events-none z-[1]"
          style={{
            background: "linear-gradient(135deg, rgba(20, 8, 2, 0.65) 0%, rgba(242, 154, 12, 0.32) 38%, rgba(35, 14, 2, 0.58) 70%, rgba(55, 68, 44, 0.72) 100%)"
          }}
        />

        {/* Golden Sunburst Radiance */}
        <div 
          className="absolute top-[20%] left-1/2 -translate-x-1/2 w-[850px] h-[580px] pointer-events-none rounded-full z-[1]"
          style={{
            background: "radial-gradient(circle, rgba(242, 154, 12, 0.42) 0%, rgba(255, 180, 0, 0.2) 45%, transparent 72%)",
            filter: "blur(60px)",
          }}
        />

        {/* Sacred Olive Green Glowing Aura Orb */}
        <motion.div
          animate={{ scale: [1, 1.15, 1], opacity: [0.25, 0.45, 0.25] }}
          transition={{ repeat: Infinity, duration: 10, ease: "easeInOut" }}
          className="absolute top-[-240px] right-[-140px] w-[800px] h-[800px] rounded-full pointer-events-none will-change-opacity z-[1]"
          style={{ background: "radial-gradient(circle, rgba(107, 138, 90, 0.35) 0%, transparent 70%)", ...gpu }}
        />

        {/* Warm Saffron Amber Ambient Orb */}
        <motion.div
          animate={{ scale: [1, 1.18, 1], opacity: [0.2, 0.4, 0.2] }}
          transition={{ repeat: Infinity, duration: 12, ease: "easeInOut", delay: 1 }}
          className="absolute bottom-[5%] left-[-100px] w-[650px] h-[650px] rounded-full pointer-events-none will-change-opacity z-[1]"
          style={{ background: "radial-gradient(circle, rgba(242, 154, 12, 0.3) 0%, transparent 70%)", ...gpu }}
        />

        {/* Floating Sacred Golden, Saffron & Olive Stardust Particles */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden z-10">
          {[...Array(24)].map((_, i) => (
            <motion.div
              key={i}
              animate={{
                y: [0, -45, 0],
                x: [0, ((i * 7) % 26) - 13, 0],
                opacity: [0.3, 0.9, 0.3],
                scale: [1, 1.35, 1],
              }}
              transition={{
                duration: 4.5 + (i % 5) * 0.8,
                repeat: Infinity,
                ease: "easeInOut",
                delay: i * 0.25,
              }}
              className="absolute rounded-full"
              style={{
                width: `${3.5 + (i % 4) * 2}px`,
                height: `${3.5 + (i % 4) * 2}px`,
                backgroundColor: i % 3 === 0 ? "#FFD166" : i % 3 === 1 ? "#F29A0C" : "#9DC08B",
                boxShadow: "0 0 12px rgba(242, 154, 12, 0.7)",
                top: `${12 + (i * 7.5) % 75}%`,
                left: `${6 + (i * 8.5) % 88}%`,
              }}
            />
          ))}
        </div>

        {/* ── HERO BOTTOM TRANSITION: WARM ESPRESSO BROWN, SAFFRON ORANGE & OLIVE GREEN OVERLAP ── */}
        <div 
          className="absolute bottom-0 left-0 right-0 h-48 pointer-events-none z-10"
          style={{
            background: "linear-gradient(to bottom, transparent 0%, rgba(32, 14, 4, 0.45) 30%, rgba(242, 154, 12, 0.35) 60%, rgba(68, 88, 54, 0.85) 88%, #FFF8EE 100%)"
          }}
        />

        {/* Radiant Accent Seam: Olive Green & Saffron Orange Edge Glow */}
        <div 
          className="absolute bottom-0 inset-x-0 h-[2.5px] pointer-events-none opacity-85 z-10"
          style={{
            background: "linear-gradient(90deg, transparent 0%, #4D6638 20%, #F29A0C 50%, #688A54 80%, transparent 100%)"
          }}
        />

        {/* Hero content, dynamically centered and responsive across all devices */}
        <motion.div 
          style={{ opacity: heroOpacity, ...gpu }} 
          className="relative z-20 w-full max-w-[1360px] mx-auto px-4 sm:px-6 lg:px-12 text-center pt-28 pb-20 sm:pt-32 sm:pb-24 flex flex-col items-center justify-center"
        >
          <motion.div className="max-w-4xl mx-auto space-y-6 sm:space-y-7" variants={stagger(0.12)} initial="hidden" animate="show">
            
            {/* Top Royal Seal Badge */}
            <motion.div variants={fadeUp} style={gpu} className="inline-flex items-center gap-3 rounded-full border border-[#F29A0C]/50 bg-black/60 backdrop-blur-2xl px-6 py-2.5 shadow-[0_10px_30px_rgba(0,0,0,0.5)]">
              <GiLotus className="text-base text-[#FFD166] animate-pulse" />
              <span className="text-[11px] sm:text-xs tracking-[3px] uppercase font-bold text-white">DIVYYOG FOUNDATION SACRED ARCHIVE</span>
            </motion.div>

            {/* Main Headline */}
            <motion.h1
              variants={fadeUp}
              style={{ fontFamily: "'Poppins', sans-serif", ...gpu }}
              className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black leading-[1.12] tracking-tight text-white drop-shadow-[0_4px_25px_rgba(0,0,0,0.95)]"
            >
              <span>Moments From Our</span>
              <span 
                className="block mt-1 sm:mt-2 bg-gradient-to-r from-[#FFF2B2] via-[#FFD166] to-[#F29A0C] bg-clip-text text-transparent"
                style={{ filter: "drop-shadow(0 4px 20px rgba(0,0,0,0.95)) drop-shadow(0 0 35px rgba(242,154,12,0.65))" }}
              >
                Foundation Community
              </span>
            </motion.h1>

            {/* Subtitle */}
            <motion.p variants={fadeUp} style={gpu} className="text-white/90 text-base sm:text-lg md:text-xl font-medium leading-[1.75] max-w-3xl mx-auto drop-shadow-[0_2px_12px_rgba(0,0,0,0.85)]">
              Captured moments from Divyyog Foundation's practice sessions, Himalayan retreats, sacred workshops, and global student assemblies.
            </motion.p>

            {/* CTA Buttons */}
            <motion.div variants={fadeUp} style={gpu} className="flex flex-wrap items-center justify-center gap-4 pt-2">
              <a href="#photo-gallery" className="no-underline">
                <motion.button
                  whileHover={{ y: -4, scale: 1.03 }}
                  style={gpu}
                  className="group relative overflow-hidden rounded-full bg-gradient-to-r from-[#F29A0C] to-[#FF7A00] text-white px-8 py-4 text-xs sm:text-sm font-bold uppercase tracking-wider shadow-[0_12px_35px_rgba(242,154,12,0.45)] cursor-pointer flex items-center gap-2.5 border-none"
                >
                  <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-all duration-1000 bg-gradient-to-r from-transparent via-white/30 to-transparent skew-x-12" />
                  <span className="relative z-20 flex items-center gap-2.5">
                    <span>Explore Photo Gallery</span>
                    <FaArrowRight size={13} />
                  </span>
                </motion.button>
              </a>

              <a href="#sacred-legacy" className="no-underline">
                <motion.button 
                  whileHover={{ y: -4 }} 
                  style={gpu} 
                  className="rounded-full border border-white/30 bg-black/55 backdrop-blur-2xl text-white px-8 py-4 text-xs sm:text-sm font-bold uppercase tracking-wider hover:bg-black/80 hover:border-[#F29A0C]/60 transition-all cursor-pointer flex items-center gap-2.5"
                >
                  <FaCrown className="text-[#FFD166]" size={13} />
                  <span>Sacred Heritage</span>
                </motion.button>
              </a>
            </motion.div>

          </motion.div>
        </motion.div>
      </section>

      {/* ══════════════════════════════════════════
          IMAGES ONLY   filter + masonry grid
      ══════════════════════════════════════════ */}
      <section id="photo-gallery" className="relative py-24 px-6 lg:px-12" style={{ background: C.cream }}>
        <GradientMesh />
        <div className="relative z-10 max-w-[1400px] mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="inline-block px-4 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-[2px] mb-4" style={{ background: `${C.orange}18`, color: C.deepOrange }}>
              Photo Gallery
            </span>
            <h2 className="text-3xl lg:text-[42px] font-semibold tracking-tight mb-4" style={{ fontFamily: "Poppins", color: C.darkAlt }}>
              Captured Moments
            </h2>
            <div className="w-16 h-1 mx-auto rounded-full" style={{ background: `linear-gradient(90deg, ${C.orange}, ${C.gold})` }} />
          </div>

          <div className="flex justify-center mb-14">
            <div className="ud-scroll ud-scroll-pad flex flex-wrap justify-start sm:justify-center gap-2 p-2 rounded-[28px] border" style={{ borderColor: `${C.orange}25`, background: "rgba(255,255,255,0.85)" }}>
              {CATEGORIES.map((cat) => (
                <FilterPill key={cat} cat={cat} active={selectedCat === cat} onClick={() => setSelectedCat(cat)} />
              ))}
            </div>
          </div>

          <motion.div ref={gridRef} layout className="columns-1 sm:columns-2 lg:columns-3 gap-7">
            <AnimatePresence mode="popLayout">
              {visibleItems.map((item, i) => (
                <motion.div key={item.id} layout exit={{ opacity: 0, scale: 0.92 }} transition={{ duration: 0.35 }}>
                  <GalleryCard item={item} index={i} onSelect={setActiveItem} />
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>

          {/* ── View More / Show Less pagination ── */}
          {filteredItems.length > pageSize && (
            <div className="mt-12 flex flex-col items-center gap-4">
              {/* Progress */}
              <div className="flex flex-col items-center gap-2 w-full max-w-[260px]">
                <p className="text-[13px] font-semibold" style={{ color: "#6B5A47" }}>
                  Showing <span style={{ color: C.orange, fontWeight: 800 }}>{Math.min(visibleCount, filteredItems.length)}</span> of{" "}
                  <span style={{ color: C.dark, fontWeight: 800 }}>{filteredItems.length}</span> moments
                </p>
                <div className="w-full h-[5px] rounded-full overflow-hidden" style={{ background: `${C.orange}22` }}>
                  <motion.div
                    className="h-full rounded-full"
                    initial={false}
                    animate={{ width: `${(Math.min(visibleCount, filteredItems.length) / filteredItems.length) * 100}%` }}
                    transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                    style={{ background: `linear-gradient(90deg, ${C.orange}, ${C.deepOrange})` }}
                  />
                </div>
              </div>

              {hasMore ? (
                <motion.button
                  type="button"
                  onClick={handleViewMore}
                  whileHover={{ y: -3, scale: 1.03 }}
                  whileTap={{ scale: 0.96 }}
                  className="group inline-flex items-center gap-2.5 px-8 py-3.5 rounded-full text-white text-[14px] font-bold cursor-pointer"
                  style={{
                    background: `linear-gradient(135deg, ${C.orange}, ${C.deepOrange})`,
                    boxShadow: "0 12px 32px rgba(242,154,12,0.35)",
                    border: "none",
                  }}
                >
                  View More
                  <span className="text-[12px] font-semibold opacity-85">
                    (+{Math.min(pageSize, filteredItems.length - visibleCount)})
                  </span>
                  <FaArrowRight className="text-[12px] rotate-90 transition-transform duration-300 group-hover:translate-y-0.5" />
                </motion.button>
              ) : canCollapse && (
                <motion.button
                  type="button"
                  onClick={handleShowLess}
                  whileHover={{ y: -3, scale: 1.03 }}
                  whileTap={{ scale: 0.96 }}
                  className="group inline-flex items-center gap-2.5 px-8 py-3.5 rounded-full text-[14px] font-bold cursor-pointer bg-white"
                  style={{ color: C.deepOrange, border: `1.5px solid ${C.orange}55`, boxShadow: "0 10px 26px rgba(242,154,12,0.15)" }}
                >
                  Show Less
                  <FaArrowRight className="text-[12px] -rotate-90 transition-transform duration-300 group-hover:-translate-y-0.5" />
                </motion.button>
              )}
            </div>
          )}
        </div>
      </section>

      {/* ══════════════════════════════════════════
          VIDEOS ONLY   featured video player + playlist (COMMENTED OUT AS REQUESTED)
      ══════════════════════════════════════════ */}
      {/* <FeaturedVideos onExpand={setActiveItem} /> */}

      {/* ══════════════════════════════════════════
          UNIQUE DESIGN AT BOTTOM: SACRED LEGACY & LIVING MEMORIES
      ══════════════════════════════════════════ */}
      <SacredLegacySection />

      {/* ══════════════════════════════════════════
          LIGHTBOX
      ══════════════════════════════════════════ */}
      <AnimatePresence>
        {activeItem && (
          <Lightbox
            activeItem={activeItem}
            items={activeItem.duration !== undefined ? VIDEO_ITEMS : IMAGE_ITEMS}
            onClose={() => setActiveItem(null)}
            onPrev={handlePrev}
            onNext={handleNext}
            onJump={setActiveItem}
          />
        )}
      </AnimatePresence>
    </div>
  );
};


// ══════════════════════════════════════════
// Unique Royal Design at Bottom: Sacred Legacy & Living Memories
// ══════════════════════════════════════════
function SacredLegacySection() {
  const sectionRef = useRef(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  const handleMouseMove = (e) => {
    if (!sectionRef.current) return;
    const rect = sectionRef.current.getBoundingClientRect();
    setMousePos({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  };

  const milestones = [
    {
      num: "10,000+",
      label: "Practitioners Worldwide",
      desc: "Dedicated sadhakas across 15+ countries discovering physical vitality, emotional calm, and inner awareness.",
      icon: FiUsers,
      color: "#F29A0C",
      badge: "15+ Nations"
    },
    {
      num: "Gold Medalist",
      label: "Pedagogy & Lineage",
      desc: "Founded upon world championship mastery, Ayush ministry guidelines, and classical Himalayan guru-shishya traditions.",
      icon: FaAward,
      color: "#FFD166",
      badge: "E-RYT 500 Certified"
    },
    {
      num: "500+",
      label: "Sacred Assemblies",
      desc: "From serene mountain dawn retreats to intensive virtual alignment clinics and community satsangs.",
      icon: FaLeaf,
      color: "#9DC08B",
      badge: "Global Footprint"
    },
    {
      num: "100%",
      label: "Authentic Sadhana",
      desc: "Uncompromising preservation of traditional Patanjali Ashtanga yoga, pranayama, and yogic mindfulness.",
      icon: FaOm,
      color: "#F29A0C",
      badge: "Vedic Roots"
    },
  ];

  return (
    <section 
      id="sacred-legacy"
      ref={sectionRef}
      onMouseMove={handleMouseMove}
      className="relative overflow-hidden py-28 px-6 lg:px-16"
      style={{
        background: "linear-gradient(135deg, rgba(20, 32, 16, 0.98) 0%, rgba(34, 48, 24, 0.95) 45%, rgba(48, 32, 14, 0.94) 80%, rgba(28, 16, 6, 0.98) 100%)",
      }}
    >
      {/* Unique Bespoke Background Image for Glimpses: Twilight Temple Sanctuary */}
      <div
        className="absolute inset-0 bg-cover bg-center opacity-65 scale-[1.03] pointer-events-none transition-transform duration-1000"
        style={{ 
          backgroundImage: "url('/assets/glimpses_bottom_bg.jpg')",
          filter: "brightness(0.75) saturate(1.15)"
        }}
      />

      {/* Deep Olive & Saffron Vignette */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/75 via-[#1E2E18]/35 to-black/85 pointer-events-none" />

      {/* Dynamic Interactive Cursor Spotlight */}
      <div
        className="absolute pointer-events-none rounded-full blur-[80px] transition-opacity duration-300 opacity-80"
        style={{
          width: "550px",
          height: "550px",
          background: "radial-gradient(circle, rgba(107,138,90,0.4) 0%, rgba(242,154,12,0.25) 45%, transparent 70%)",
          left: `${mousePos.x - 275}px`,
          top: `${mousePos.y - 275}px`,
        }}
      />

      {/* Rotating Sacred Mandala Watermark in Corner */}
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 75, repeat: Infinity, ease: "linear" }}
        className="absolute -right-24 -bottom-24 w-[460px] h-[460px] opacity-15 text-[#FFD166] pointer-events-none"
      >
        <FaOm className="w-full h-full" />
      </motion.div>

      {/* Top Animated Golden Shimmer Seam */}
      <div
        className="absolute top-0 left-0 right-0 h-[3px]"
        style={{
          background: "linear-gradient(90deg, transparent, #4D6638, #F29A0C, #FFD166, #F29A0C, #4D6638, transparent)",
        }}
      />

      {/* Bottom Shimmer Seam */}
      <div
        className="absolute bottom-0 left-0 right-0 h-[3px]"
        style={{
          background: "linear-gradient(90deg, transparent, #F29A0C, #4D6638, #FFD166, #4D6638, #F29A0C, transparent)",
        }}
      />

      <div className="relative z-10 max-w-[1360px] mx-auto">
        
        {/* HEADER */}
        <div className="text-center max-w-3xl mx-auto mb-20 space-y-4">
          <span className="inline-flex items-center gap-2.5 px-5 py-2 rounded-full bg-black/60 border border-[#F29A0C]/40 text-[#FFD166] text-[11px] font-black uppercase tracking-[3px] shadow-lg">
            <GiLotus className="text-base" />
            THE LIVING TAPESTRY OF SADHANA
          </span>

          <h2 
            className="text-3xl sm:text-5xl font-black text-white tracking-tight leading-tight"
            style={{ fontFamily: "'Poppins', sans-serif" }}
          >
            Every Frame Reflects A Journey Of Awakening
          </h2>

          <p className="text-white/80 text-sm sm:text-base font-normal max-w-2xl mx-auto leading-relaxed">
            From sunrise pranayama on sacred Himalayan banks to heartfelt student gatherings, explore the authentic spirit of Divyyog Foundation.
          </p>
        </div>

        {/* 4 JEWEL IMPACT CARDS */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
          {milestones.map((m, idx) => {
            const Icon = m.icon;
            return (
              <motion.div
                key={idx}
                whileHover={{ y: -10, transition: { duration: 0.3 } }}
                className="group relative rounded-[32px] p-7 bg-black/55 backdrop-blur-2xl border border-white/15 hover:border-[#F29A0C]/70 hover:bg-black/70 transition-all duration-500 shadow-2xl flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-center justify-between gap-3 mb-6">
                    <div 
                      className="w-13 h-13 rounded-2xl flex items-center justify-center shadow-lg transition-transform duration-300 group-hover:scale-110"
                      style={{
                        background: `linear-gradient(135deg, ${m.color}35, ${m.color}10)`,
                        border: `1.5px solid ${m.color}65`,
                        color: m.color
                      }}
                    >
                      <Icon size={22} />
                    </div>

                    <span 
                      className="text-[9px] uppercase tracking-widest font-black px-3 py-1 rounded-full border"
                      style={{
                        background: "rgba(255,255,255,0.06)",
                        borderColor: `${m.color}45`,
                        color: m.color
                      }}
                    >
                      {m.badge}
                    </span>
                  </div>

                  <h3 
                    className="text-2xl sm:text-3xl font-black text-white mb-1.5 tracking-tight group-hover:text-[#FFD166] transition-colors"
                    style={{ fontFamily: "'Poppins', sans-serif" }}
                  >
                    {m.num}
                  </h3>

                  <h4 className="text-xs uppercase font-bold tracking-wider text-[#FFD166] mb-3">
                    {m.label}
                  </h4>

                  <p className="text-white/75 text-xs leading-relaxed font-normal">
                    {m.desc}
                  </p>
                </div>

                <div className="mt-6 pt-4 border-t border-white/10 flex items-center gap-1.5 text-[11px] font-bold text-white/50 group-hover:text-white transition-colors">
                  <FiCheckCircle size={13} style={{ color: m.color }} />
                  <span>Verified Sadhana Archive</span>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* FOUNDER'S SACRED PHILOSOPHY BANNER */}
        <div className="p-8 sm:p-12 rounded-[38px] bg-gradient-to-br from-[#203318]/95 via-[#3D5428]/90 to-[#9E530C]/85 border-2 border-[#F29A0C]/50 backdrop-blur-2xl shadow-[0_25px_80px_rgba(0,0,0,0.45)] relative overflow-hidden">
          
          {/* Ambient Olive & Radiant Orange Glow Orbs */}
          <div className="absolute -top-16 -left-16 w-72 h-72 rounded-full bg-[radial-gradient(circle,rgba(107,138,90,0.45)_0%,transparent_70%)] pointer-events-none" />
          <div className="absolute -bottom-16 -right-16 w-80 h-80 rounded-full bg-[radial-gradient(circle,rgba(242,154,12,0.4)_0%,transparent_70%)] pointer-events-none" />

          {/* Top Golden Shimmer Line */}
          <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#FFD166] to-transparent opacity-70" />

          <div className="absolute top-0 right-0 translate-x-8 -translate-y-8 text-white/[0.05] pointer-events-none text-9xl">
            <FaQuoteLeft />
          </div>

          <div className="relative z-10 flex flex-col lg:flex-row items-center justify-between gap-8">
            <div className="space-y-4 text-center lg:text-left max-w-3xl">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#6B8A5A]/30 border border-[#6B8A5A]/60 text-[#FFD166] text-xs font-bold uppercase tracking-widest backdrop-blur-md">
                <FaCrown size={13} className="text-[#FFD166]" />
                <span>Foundational Philosophy</span>
              </div>

              <blockquote 
                className="text-lg sm:text-2xl text-white font-medium italic leading-relaxed"
                style={{ fontFamily: "'Poppins', sans-serif" }}
              >
                &ldquo;Yoga is not merely about achieving an aesthetic posture; it is the sacred rediscovery of the quiet sanctuary that already exists within you. Every image in our archive reflects a soul choosing awareness, vitality, and peace.&rdquo;
              </blockquote>

              <div className="pt-2">
                <p className="text-white font-bold text-sm sm:text-base">
                  Mrs. Divya Nithin Bhojwani
                </p>
                <p className="text-[#FFD166] text-xs font-medium">
                  Founder, International Gold Medalist &amp; Lead Acharya
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-4 shrink-0">
              <Link 
                to="/live-classes"
                className="w-full sm:w-auto px-8 py-4 rounded-full bg-gradient-to-r from-[#F29A0C] to-[#FFA726] text-white font-bold text-xs uppercase tracking-wider shadow-lg hover:scale-105 transition-transform no-underline inline-flex items-center justify-center gap-2"
              >
                <span>Join Live Class</span>
                <FaArrowRight size={12} />
              </Link>

              <Link 
                to="/pricing"
                className="w-full sm:w-auto px-8 py-4 rounded-full bg-white/10 hover:bg-white/20 text-white border border-white/25 font-bold text-xs uppercase tracking-wider transition-all no-underline inline-flex items-center justify-center gap-2"
              >
                <span>View Memberships</span>
              </Link>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}

export default Gallery;